import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import axios from 'axios';
import { 
  Settings, Save, RotateCcw, Eye, EyeOff, GripVertical,
  HelpCircle, ChevronDown, ChevronRight, Check, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_BLUE = '#2b3380';

const CQCConfiguration = ({ user, onLogout }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [expandedParams, setExpandedParams] = useState({});
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/qc-stage/cqc/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to load CQC config:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/qc-stage/cqc/config`, {
        parameters: config.parameters
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasChanges(false);
      alert('Configuration saved successfully!');
    } catch (error) {
      console.error('Failed to save config:', error);
      alert('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const resetConfig = async () => {
    if (!window.confirm('Reset to default configuration? This will overwrite your changes.')) {
      return;
    }
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/qc-stage/cqc/config/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data.config);
      setHasChanges(false);
    } catch (error) {
      console.error('Failed to reset config:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleParameter = (paramId) => {
    const newParams = config.parameters.map(p => {
      if (p.parameter_id === paramId) {
        return { ...p, enabled: !p.enabled };
      }
      return p;
    });
    setConfig({ ...config, parameters: newParams });
    setHasChanges(true);
  };

  const updateParameterLabel = (paramId, newLabel) => {
    const newParams = config.parameters.map(p => {
      if (p.parameter_id === paramId) {
        return { ...p, simplified_label: newLabel };
      }
      return p;
    });
    setConfig({ ...config, parameters: newParams });
    setHasChanges(true);
  };

  const updateParameterHelpText = (paramId, helpText) => {
    const newParams = config.parameters.map(p => {
      if (p.parameter_id === paramId) {
        return { ...p, help_text: helpText };
      }
      return p;
    });
    setConfig({ ...config, parameters: newParams });
    setHasChanges(true);
  };

  const updateOptionLabel = (paramId, optionId, newLabel) => {
    const newParams = config.parameters.map(p => {
      if (p.parameter_id === paramId) {
        const newOptions = p.simplified_options.map(o => {
          if (o.id === optionId) {
            return { ...o, label: newLabel };
          }
          return o;
        });
        return { ...p, simplified_options: newOptions };
      }
      return p;
    });
    setConfig({ ...config, parameters: newParams });
    setHasChanges(true);
  };

  const updateOptionDeduction = (paramId, optionId, deduction) => {
    const newParams = config.parameters.map(p => {
      if (p.parameter_id === paramId) {
        const newOptions = p.simplified_options.map(o => {
          if (o.id === optionId) {
            return { ...o, deduction_pct: parseInt(deduction) || 0 };
          }
          return o;
        });
        return { ...p, simplified_options: newOptions };
      }
      return p;
    });
    setConfig({ ...config, parameters: newParams });
    setHasChanges(true);
  };

  const toggleExpand = (paramId) => {
    setExpandedParams(prev => ({
      ...prev,
      [paramId]: !prev[paramId]
    }));
  };

  // Group parameters by category
  const groupedParams = config?.parameters?.reduce((acc, param) => {
    const cat = param.category_name || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(param);
    return acc;
  }, {}) || {};

  const enabledCount = config?.parameters?.filter(p => p.enabled).length || 0;
  const totalCount = config?.parameters?.length || 0;

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: ZOBOX_BLUE }}>
              <Settings className="inline-block mr-2 h-6 w-6" />
              CQC Configuration
            </h1>
            <p className="text-gray-600 mt-1">
              Configure customer-facing QC questions for the website
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setPreviewMode(!previewMode)}
              className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              <Eye className="h-4 w-4" />
              {previewMode ? 'Edit Mode' : 'Preview'}
            </button>
            <button
              onClick={resetConfig}
              className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 text-orange-600"
            >
              <RotateCcw className="h-4 w-4" />
              Reset
            </button>
            <button
              onClick={saveConfig}
              disabled={!hasChanges || saving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-white disabled:opacity-50"
              style={{ backgroundColor: ZOBOX_BLUE }}
              data-testid="save-cqc-config-btn"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="text-3xl font-bold" style={{ color: ZOBOX_BLUE }}>{enabledCount}</div>
            <div className="text-gray-600">Enabled Questions</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="text-3xl font-bold text-gray-400">{totalCount - enabledCount}</div>
            <div className="text-gray-600">Hidden Questions</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <div className="text-3xl font-bold text-green-600">v{config?.version || '1.0'}</div>
            <div className="text-gray-600">Config Version</div>
          </div>
        </div>

        {hasChanges && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6 flex items-center gap-2">
            <span className="text-yellow-600">⚠️</span>
            <span className="text-yellow-800">You have unsaved changes</span>
          </div>
        )}

        {/* Preview Mode */}
        {previewMode ? (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-xl font-semibold mb-4">Customer View Preview</h2>
            <div className="max-w-lg mx-auto bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-medium mb-4 text-center">Tell us about your phone</h3>
              {config?.parameters?.filter(p => p.enabled).sort((a, b) => a.order - b.order).map(param => (
                <div key={param.parameter_id} className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {param.simplified_label}
                    {param.help_text && (
                      <span className="ml-1 text-gray-400 cursor-help" title={param.help_text}>
                        <HelpCircle className="inline h-3 w-3" />
                      </span>
                    )}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {param.simplified_options?.map(opt => (
                      <button
                        key={opt.id}
                        className="p-2 border rounded-lg text-sm hover:border-blue-500 hover:bg-blue-50 text-left"
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* Edit Mode */
          <div className="space-y-4">
            {Object.entries(groupedParams).map(([category, params]) => (
              <div key={category} className="bg-white rounded-lg shadow-sm border overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b flex items-center justify-between">
                  <h3 className="font-medium text-gray-800">{category}</h3>
                  <span className="text-sm text-gray-500">
                    {params.filter(p => p.enabled).length} / {params.length} enabled
                  </span>
                </div>
                <div className="divide-y">
                  {params.map(param => (
                    <div key={param.parameter_id} className="p-4">
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => toggleParameter(param.parameter_id)}
                          className={`p-2 rounded-lg ${param.enabled ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                          data-testid={`toggle-${param.parameter_id}`}
                        >
                          {param.enabled ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        </button>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-500">{param.original_name}</span>
                            <span className="text-gray-300">→</span>
                          </div>
                          <input
                            type="text"
                            value={param.simplified_label || ''}
                            onChange={(e) => updateParameterLabel(param.parameter_id, e.target.value)}
                            className="w-full border rounded px-3 py-1 mt-1 text-sm"
                            placeholder="Customer-friendly question..."
                            disabled={!param.enabled}
                          />
                        </div>
                        <button
                          onClick={() => toggleExpand(param.parameter_id)}
                          className="p-2 hover:bg-gray-100 rounded"
                        >
                          {expandedParams[param.parameter_id] ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                      
                      {expandedParams[param.parameter_id] && param.enabled && (
                        <div className="mt-4 pl-12 space-y-3">
                          {/* Help Text */}
                          <div>
                            <label className="text-xs text-gray-500 mb-1 block">Help Text (tooltip)</label>
                            <input
                              type="text"
                              value={param.help_text || ''}
                              onChange={(e) => updateParameterHelpText(param.parameter_id, e.target.value)}
                              className="w-full border rounded px-3 py-1 text-sm"
                              placeholder="Help text shown to customer..."
                            />
                          </div>
                          
                          {/* Simplified Options */}
                          <div>
                            <label className="text-xs text-gray-500 mb-2 block">Answer Options & Deductions</label>
                            <div className="space-y-2">
                              {param.simplified_options?.map(opt => (
                                <div key={opt.id} className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    value={opt.label || ''}
                                    onChange={(e) => updateOptionLabel(param.parameter_id, opt.id, e.target.value)}
                                    className="flex-1 border rounded px-3 py-1 text-sm"
                                  />
                                  <div className="flex items-center gap-1">
                                    <span className="text-xs text-gray-500">-</span>
                                    <input
                                      type="number"
                                      value={opt.deduction_pct || 0}
                                      onChange={(e) => updateOptionDeduction(param.parameter_id, opt.id, e.target.value)}
                                      className="w-16 border rounded px-2 py-1 text-sm text-center"
                                      min="0"
                                      max="100"
                                    />
                                    <span className="text-xs text-gray-500">%</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CQCConfiguration;
